'use client'

import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from '@/components/ui/icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  /** Maximum number of auto-reload attempts before showing circuit breaker */
  maxReloadAttempts?: number;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: React.ErrorInfo;
  /** Circuit breaker: track reload attempts */
  reloadCount: number;
  /** Circuit breaker: show manual recovery UI */
  showCircuitBreaker: boolean;
}

/** Maximum auto-reload attempts before circuit breaker activates */
const DEFAULT_MAX_RELOAD_ATTEMPTS = 3;

/**
 * Error Boundary Component for React Applications
 * Catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the entire application
 */
export class ErrorBoundary extends Component<Props, State> {
  private reloadTimeoutIds: ReturnType<typeof setTimeout>[] = [];

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      reloadCount: 0,
      showCircuitBreaker: false
    };
  }

  componentWillUnmount() {
    // Clean up any pending timeouts to prevent memory leaks
    this.reloadTimeoutIds.forEach(id => clearTimeout(id));
  }

  /** Check if we've exceeded max reload attempts */
  private hasReachedMaxReloadAttempts = (): boolean => {
    const maxAttempts = this.props.maxReloadAttempts ?? DEFAULT_MAX_RELOAD_ATTEMPTS;
    return this.state.reloadCount >= maxAttempts;
  };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      reloadCount: 0,
      showCircuitBreaker: false
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Special handling for React Error #130 (Objects are not valid as React children)
    if (error.message.includes('Objects are not valid as a React child') || 
        error.message.includes('Minified React error #130')) {
      console.error('React Error #130 detected! This usually means an object is being rendered instead of a string.');
      console.error('Common causes:');
      console.error('1. Translation function returning objects during SSR');
      console.error('2. API response not properly handled');
      console.error('3. Nested objects being rendered in JSX');
      console.error('4. i18n not fully initialized during server-side rendering');
      console.error('Error details:', error, errorInfo);
      
      // Implement aggressive remediation for React Error #130
      if (typeof window !== 'undefined') {
        // Circuit breaker: Check if we've exceeded max reload attempts
        this.setState(prevState => {
          const maxAttempts = this.props.maxReloadAttempts ?? DEFAULT_MAX_RELOAD_ATTEMPTS;
          const newCount = prevState.reloadCount + 1;
          if (newCount >= maxAttempts) {
            // Clear all pending reloads
            this.reloadTimeoutIds.forEach(id => clearTimeout(id));
            this.reloadTimeoutIds = [];
            return { reloadCount: newCount, showCircuitBreaker: true } as State;
          }
          return { reloadCount: newCount, showCircuitBreaker: false } as State;
        });

        try {
          // Clear any potential cached problematic components
          if ('caches' in window) {
            caches.keys().then(names => {
              names.forEach(name => {
                caches.delete(name);
              });
            });
          }

          // Set a flag to prevent re-occurrence
          sessionStorage.setItem('joyous_react_130_recovered', 'true');

          const timeoutId1 = setTimeout(() => {
            // Force a complete page reload with cache clear
            window.location.href = window.location.href + '?react_130_fix=' + Date.now();
          }, 1500);
          this.reloadTimeoutIds.push(timeoutId1);
        } catch (remediationError) {
          console.error('Error during React Error #130 remediation:', remediationError);
          const timeoutId2 = setTimeout(() => {
            window.location.reload();
          }, 2000);
          this.reloadTimeoutIds.push(timeoutId2);
        }
      }
    }

    // Special handling for React Error #418 (HTML elements cannot be used as React children)
    if (error.message.includes('HTML elements cannot be used as React children') || 
        error.message.includes('Minified React error #418')) {
      console.error('React Error #418 detected! This usually means HTML content is being passed directly as React children.');
      console.error('Common causes:');
      console.error('1. Raw HTML elements being rendered instead of strings');
      console.error('2. Rich text editor content not properly sanitized');
      console.error('3. dangerouslySetInnerHTML content being used incorrectly');
      console.error('4. DOM elements passed as JSX children');
      console.error('Error details:', error, errorInfo);
      
      // Implement aggressive remediation for React Error #418
      if (typeof window !== 'undefined') {
        // Circuit breaker: Check if we've exceeded max reload attempts
        this.setState(prevState => {
          const maxAttempts = this.props.maxReloadAttempts ?? DEFAULT_MAX_RELOAD_ATTEMPTS;
          const newCount = prevState.reloadCount + 1;
          if (newCount >= maxAttempts) {
            // Clear all pending reloads
            this.reloadTimeoutIds.forEach(id => clearTimeout(id));
            this.reloadTimeoutIds = [];
            return { reloadCount: newCount, showCircuitBreaker: true } as State;
          }
          return { reloadCount: newCount, showCircuitBreaker: false } as State;
        });

        try {
          // Clear browser caches that might contain problematic content
          if ('caches' in window) {
            caches.keys().then(names => {
              names.forEach(name => {
                if (name.includes('react') || name.includes('joyous')) {
                  caches.delete(name);
                }
              });
            });
          }

          // Set recovery flag
          sessionStorage.setItem('joyous_react_418_recovered', 'true');

          // Clear localStorage items that might contain problematic data
          try {
            const keysToRemove = [];
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && (key.includes('translation') || key.includes('i18n') || key.includes('joyous'))) {
                keysToRemove.push(key);
              }
            }
            keysToRemove.forEach(key => localStorage.removeItem(key));
          } catch (storageError) {
            console.warn('Could not clear localStorage:', storageError);
          }

          const timeoutId3 = setTimeout(() => {
            // Force page reload with cache busting
            window.location.href = window.location.href + '?react_418_fix=' + Date.now();
          }, 2500);
          this.reloadTimeoutIds.push(timeoutId3);
        } catch (remediationError) {
          console.error('Error during React Error #418 remediation:', remediationError);
          const timeoutId4 = setTimeout(() => {
            window.location.reload();
          }, 3000);
          this.reloadTimeoutIds.push(timeoutId4);
        }
      }
    }

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  componentDidMount() {
    // Check for recovery flags on component mount
    if (typeof window !== 'undefined') {
      try {
        const react130Recovered = sessionStorage.getItem('joyous_react_130_recovered');
        const react418Recovered = sessionStorage.getItem('joyous_react_418_recovered');
        
        if (react130Recovered || react418Recovered) {

          // Clear recovery flags
          sessionStorage.removeItem('joyous_react_130_recovered');
          sessionStorage.removeItem('joyous_react_418_recovered');

          // Force a fresh page load to ensure clean state
          const timeoutId5 = setTimeout(() => {
            const url = new URL(window.location.href);
            url.searchParams.delete('react_130_fix');
            url.searchParams.delete('react_418_fix');
            window.location.replace(url.toString());
          }, 100);
          this.reloadTimeoutIds.push(timeoutId5);
        }
      } catch (error) {
        console.warn('Error checking recovery flags:', error);
      }
    }
  }

  handleGoHome = () => {
    if (typeof window !== 'undefined') {
      // Use basePath-aware navigation
      window.location.href = '/';
    }
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Circuit breaker UI - show when auto-recovery has been exhausted
      if (this.state.showCircuitBreaker) {
        return (
          <div className="min-h-screen bg-gray-50 dark:bg-gray-800 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
              <CardHeader className="text-center">
                <div className="mx-auto w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                  Recovery Limit Reached
                </CardTitle>
                <CardDescription>
                  Automatic recovery attempted {this.state.reloadCount} times but the error persists.
                  Please try one of the manual recovery options below.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm text-amber-800">
                  <p className="font-medium mb-2">Suggested actions:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Clear your browser cache and cookies</li>
                    <li>Try accessing the page in an incognito window</li>
                    <li>Contact support if the issue persists</li>
                  </ul>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={this.handleReset}
                    variant="outline"
                    className="flex-1"
                  >
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    onClick={this.handleGoHome}
                    className="flex-1"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Go Home
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
      }

      // Check if this is React Error #130 (both full and minified versions)
      const isReactError130 = this.state.error?.message?.includes('Objects are not valid as a React child') ||
                              this.state.error?.message?.includes('Minified React error #130');

      // Check if this is React Error #418 (both full and minified versions)
      const isReactError418 = this.state.error?.message?.includes('HTML elements cannot be used as React children') ||
                              this.state.error?.message?.includes('Minified React error #418');

      // Show recovery attempt counter
      const attemptInfo = this.state.reloadCount > 0
        ? ` (Attempt ${this.state.reloadCount} of ${this.props.maxReloadAttempts ?? DEFAULT_MAX_RELOAD_ATTEMPTS})`
        : '';

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-800 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                {isReactError130 ? 'Loading Issue Detected' : isReactError418 ? 'Content Rendering Issue' : 'Something went wrong'}{attemptInfo}
              </CardTitle>
              <CardDescription>
                {isReactError130
                  ? 'The page encountered a loading issue. Attempting automatic recovery...'
                  : isReactError418
                  ? 'The page encountered a content rendering issue. Attempting automatic recovery...'
                  : 'An unexpected error occurred. Please try refreshing the page or contact support if the problem persists.'
                }
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="bg-gray-100 dark:bg-gray-700 p-3 rounded-md text-sm">
                  <summary className="cursor-pointer font-medium text-gray-700 dark:text-gray-200 mb-2">
                    Error Details (Development Only)
                  </summary>
                  <div className="text-red-600 dark:text-red-400 font-mono text-xs whitespace-pre-wrap">
                    {this.state.error.message}
                    {this.state.errorInfo?.componentStack}
                  </div>
                </details>
              )}
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={this.handleReset}
                  variant="outline"
                  className="flex-1"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
                <Button
                  onClick={this.handleGoHome}
                  className="flex-1"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook for manual error handling in functional components
 */
export const useErrorHandler = () => {
  return (error: Error, errorInfo?: string) => {
    // Log error for monitoring service integration
    console.error('Manual error caught:', error, errorInfo);
    
    // Here you could integrate with error monitoring services like:
    // - Sentry
    // - LogRocket
    // - Bugsnag
    // - Custom error tracking service
    
    // Example integration point
    if (typeof window !== 'undefined' && (window as any).errorTracking) {
      (window as any).errorTracking.captureException(error, {
        extra: { errorInfo }
      });
    }
  };
};

/**
 * Higher-order component for wrapping components with error boundary
 */
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

export default ErrorBoundary;